import {CountryCode} from "~/constants/countryCode";
import {
  GREEN_CLAIM_FALLBACK_LANGUAGE,
  GREEN_CLAIM_FUTURE_YEAR_LOOKAHEAD,
  GREEN_CLAIM_LANGUAGES_BY_COUNTRY,
  GREEN_CLAIM_PLURAL_S_LANGUAGES,
  GREEN_CLAIM_SEVERITIES,
  GREEN_CLAIM_TERMS,
  GreenClaimLanguage,
  IGreenClaimTerms,
} from "~/constants/greenClaims";

export type GreenClaimSeverity = keyof IGreenClaimTerms;

export interface IGreenClaimMatch {
  term: string;
  start: number;
  end: number;
  severity: GreenClaimSeverity;
}

export interface IGreenClaimSummary {
  count: number;
  terms: string[];
  hasGroup1Claim: boolean;
  strictTerms: string[];
}

interface ISelectedGreenClaimTerm {
  term: string;
  severity: GreenClaimSeverity;
  allowPluralS: boolean;
}

interface ICompiledGreenClaimTerm {
  term: string;
  severity: GreenClaimSeverity;
  regExp: RegExp;
}

const WORD_CHARACTER_PATTERN = "[\\p{L}\\p{N}]";
const wordCharacterRegExp = new RegExp(WORD_CHARACTER_PATTERN, "u");

// The shop country language, plus English which Makers use across every market.
export const getGreenClaimLanguagesForCountry = (country: CountryCode): GreenClaimLanguage[] =>
  Array.from(new Set([...(GREEN_CLAIM_LANGUAGES_BY_COUNTRY[country] || []), GREEN_CLAIM_FALLBACK_LANGUAGE]));

const compiledTermsCache = new Map<string, ICompiledGreenClaimTerm[]>();

const normalizeApostrophes = (text: string) => text.replace(/[\u2018\u2019\u02bc]/g, "'");
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const isWordCharacter = (character: string | undefined) => !!character && wordCharacterRegExp.test(character);
const getMatchLength = ({start, end}: IGreenClaimMatch) => end - start;

const execAll = (regExp: RegExp, text: string): RegExpExecArray[] => {
  regExp.lastIndex = 0;

  const matches: RegExpExecArray[] = [];
  let match = regExp.exec(text);

  while (match) {
    matches.push(match);
    match = regExp.exec(text);
  }

  return matches;
};

const compileTerm = (term: string, severity: GreenClaimSeverity, allowPluralS: boolean): ICompiledGreenClaimTerm => {
  // hyphens and spaces are interchangeable, so "eco-friendly" also matches "eco friendly"
  const body = escapeRegExp(normalizeApostrophes(term)).replace(/[-\s]+/g, "[-\\s]+");
  const plural = allowPluralS ? "s?" : "";

  return {
    term,
    severity,
    regExp: new RegExp(`${body}${plural}(?!${WORD_CHARACTER_PATTERN})`, "giu"),
  };
};

const getTermKey = (term: string, allowPluralS: boolean) =>
  `${normalizeApostrophes(term)
    .toLowerCase()
    .replace(/[-\s]+/g, " ")}|${allowPluralS}`;

const getCompiledTerms = (languages: GreenClaimLanguage[]): ICompiledGreenClaimTerm[] => {
  const uniqueLanguages = Array.from(new Set(languages)).sort();
  const cacheKey = uniqueLanguages.join(",");
  const cachedTerms = compiledTermsCache.get(cacheKey);

  if (cachedTerms) return cachedTerms;

  const selectedTerms = new Map<string, ISelectedGreenClaimTerm>();

  uniqueLanguages.forEach((language) => {
    const allowPluralS = GREEN_CLAIM_PLURAL_S_LANGUAGES.includes(language);

    GREEN_CLAIM_SEVERITIES.forEach((severity) =>
      GREEN_CLAIM_TERMS[language][severity].forEach((term) => {
        const key = getTermKey(term, allowPluralS);
        const existingTerm = selectedTerms.get(key);

        // the same word appears in several languages; when the groups disagree the stricter wins
        if (!existingTerm || (existingTerm.severity === "group1" && severity === "group2")) {
          selectedTerms.set(key, {term, severity, allowPluralS});
        }
      }),
    );
  });

  const compiledTerms = Array.from(selectedTerms.values())
    // longest first, so that "green choice" is preferred over the "green" it contains
    .sort((a, b) => b.term.length - a.term.length)
    .map(({term, severity, allowPluralS}) => compileTerm(term, severity, allowPluralS));

  compiledTermsCache.set(cacheKey, compiledTerms);

  return compiledTerms;
};

const hasFutureYearNearby = (text: string, fromIndex: number) => {
  const lookahead = text.slice(fromIndex, fromIndex + GREEN_CLAIM_FUTURE_YEAR_LOOKAHEAD);
  const currentYear = new Date().getFullYear();

  return (lookahead.match(/\b\d{4}\b/g) || []).some((year) => Number(year) > currentYear);
};

// Finds every flagged environmental word in the visible text. Advisory only - the result never
export const findGreenClaims = (text: string, languages: GreenClaimLanguage[]): IGreenClaimMatch[] => {
  if (!text || !languages.length) return [];

  const normalizedText = normalizeApostrophes(text);

  const candidates = getCompiledTerms(languages).flatMap<IGreenClaimMatch>(({term, severity, regExp}) =>
    execAll(regExp, normalizedText)
      .filter(({index}) => !isWordCharacter(normalizedText[index - 1]))
      .map(({index, 0: matched}) => ({
        term,
        start: index,
        end: index + matched.length,
        severity: hasFutureYearNearby(normalizedText, index + matched.length) ? "group2" : severity,
      })),
  );

  return candidates
    .sort((a, b) => a.start - b.start || getMatchLength(b) - getMatchLength(a))
    .reduce<IGreenClaimMatch[]>((accepted, candidate) => {
      const previous = accepted[accepted.length - 1];

      // accepted matches never overlap, so only the last one can reach the candidate
      return previous && candidate.start < previous.end ? accepted : [...accepted, candidate];
    }, []);
};

export const summarizeGreenClaims = (matches: IGreenClaimMatch[]): IGreenClaimSummary => {
  const termsByKey = new Map<string, string>();
  const strictTermsByKey = new Map<string, string>();

  matches.forEach(({term, severity}) => {
    const key = term.toLowerCase();
    if (!termsByKey.has(key)) termsByKey.set(key, term);
    if (severity === "group2" && !strictTermsByKey.has(key)) strictTermsByKey.set(key, term);
  });

  const terms = Array.from(termsByKey.values());

  return {
    count: terms.length,
    terms,
    hasGroup1Claim: matches.some(({severity}) => severity === "group1"),
    strictTerms: Array.from(strictTermsByKey.values()),
  };
};
